<template>
  <div class="forgot-password-wrapper main-view">
    <div v-if="stage === 'input'" class="container single-card-container">
      <div class="row">
        <div class="card-col">
          <div class="forgot-password-card" data-aos="fade-in">
            <h2 class="forgot-password-title text-primary">{{ 'password_forgot_title' | translate('passwords') }}</h2>
            <p v-show="errorMsg === 'phoneNotFound'" class="text-danger">{{ $t('password_forgot_phone_not_found_error') }}</p>
            <p v-show="invalidPhone" class="text-danger">{{ $t('register_invalid_phone_number') }}</p>
            <form class="mt-4" action="">
              <b-form-group :label="$t('register_phone_number')">

                <input v-model="phone"
                      id="phone"
                      :placeholder="$t('register_phone_number')"
                      @keydown.enter.prevent="validated ? manageInput() : () => {}"
                      @input="validate()"
                      ref="phone"
                      type="text" class="form-control rounded-pill">
              </b-form-group>
              <div class="row">
                <div class="col-5">
                  <a @click="manageBack()" class="btn btn-light font-weight-light py-3 d-flex justify-content-center">{{$t('back')}}</a>
                </div>
                <div class="col-7">
                  <a @click="manageInput()" :class="(validated ? '' : 'disabled ') + 'btn text-primary font-weight-light btn-yellow py-3 d-flex justify-content-center'">{{ $t('continue') }}</a>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
    <div v-else-if="stage === 'confirmation'" class="container single-card-container">
      <div class="row">
        <div class="card-col">
          <div class="forgot-password-card" data-aos="fade-in">
            <p v-show="errorMsg === 'codeNotFound'" class="text-danger">{{ $t('register_code_not_found') }}</p>
            <p v-show="errorMsg === 'codeExpired'" class="text-danger">{{ $t('register_code_expired') }}</p>
            <h2 class="forgot-password-title text-primary">{{$t('register_confirmation_code')}}</h2>
            <p>{{$t('phone_confirmation_code_sent')}}</p>
            <form class="mt-4" action="">
              <b-form-group :label="$t('register_confirmation_code')">
                <input v-model="confirmationCode" 
                       id="confirmation"
                       type="text"
                       class="form-control rounded-pill"
                       :placeholder="$t('register_confirmation_code')"
                       style="text-transform:uppercase"
                       @input="validateConfirm()"
                       @keydown.enter.prevent="legalConfirm ? manageConfirm() : () => {}">
              </b-form-group>
              <div class="row">
                <div class="col-5">
                  <a @click="manageBack()" class="btn btn-light font-weight-light py-3 d-flex justify-content-center">{{$t('back')}}</a>
                </div>
                <div class="col-7">
                  <a @click="manageConfirm()" :class="(legalConfirm ? '' : 'disabled ') + 'btn text-primary font-weight-light btn-yellow py-3 d-flex justify-content-center'">{{$t('continue')}}</a>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
    <div v-else-if="stage === 'password'" class="container single-card-container">
      <div class="row">
        <div class="card-col">
          <div class="forgot-password-card" data-aos="fade-in">
            <p v-if="errorMsg === 'passwordError'" class="text-danger">{{ $t('password_forgot_error') }}</p>
            <h2 class="forgot-password-title text-primary">{{$t('register_input_data')}}</h2>
            <form class="mt-4" action="">
              <b-form-group :label="$t('register_phone_number')">
                <input v-model="user.phone"
                       disabled
                       id="tel"
                       ref="user-phone"
                       type="tel"
                       class="form-control rounded-pill"
                       :placeholder="$t('register_phone_number')">
              </b-form-group>
              <p v-show="tooShort" class="text-danger">{{ $t('register_password_too_short') }}</p>
              <b-form-group :label="$t('password')">
                <input v-model="user.password"
                       id="password"
                       ref="user-password"
                       type="password"
                       class="form-control rounded-pill"
                       :placeholder="$t('password')"
                       @input="validatePassword()"
                       @keydown.enter.prevent="$refs.user-password-confirmation.focus()"
                >
              </b-form-group>
              <p v-show="noMatch" class="text-danger">{{ $t('register_password_not_matching') }}</p>
              <b-form-group :label="$t('register_repeat_password')">
                <input v-model="user.password_confirmation"
                       id="password2"
                       ref="user-password-confirmation"
                       type="password"
                       class="form-control rounded-pill"
                       :placeholder="$t('register_repeat_password')"
                       @input="validatePassword()"
                       @keydown.enter.prevent="passwordValidated ? managePassword() : () => {}">
              </b-form-group>
              <div class="row">
                <div class="col-5">
                  <a @click="manageBack()" class="btn btn-light font-weight-light py-3 d-flex justify-content-center">{{ $t('back') }}</a>
                </div>
                <div class="col-7">
                  <a @click="managePassword()" :class="(passwordValidated ? '' : 'disabled ') + 'btn text-primary font-weight-light btn-yellow py-3 d-flex justify-content-center'">{{$t('continue')}}</a>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
      <b-modal
          :title="$t('password_forgot_success_title')"
          @hide="onConfirmModalClose"
          @close="onConfirmModalClose"
          ref="forgot-password-modal-confirm"
          >
        <template v-slot:modal-footer>
          <button type="button" class="btn btn-light px-4 py-2" @click="onConfirmModalClose()">{{$t('modal_close')}}</button>
        </template>
      </b-modal>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import VuexTypes from '@/store/types/index';
import Vue from 'vue';

const AuthTypes = VuexTypes.auth;
export default {
  mounted() {
    this.$refs.phone.focus();
  },
  data() {
    return {
      phone: '',
      emailSent: false,
      errorMsg: {},
      sendSpinner: false,
      stage: 'input',
      invalidPhone: false,
      validated: false,
      errorMsg: '',
      submitType: 'phone',
      legalConfirm: false,
      confirmationCode: '',
      passwordValidated: false,
      tooShort: false,
      noMatch: false,
      showModal:false,
      user: {
        phone: '',
        password: '',
        password_confirmation: ''
      }
    };
  },
  computed: {
  },
  watch: {
  },
  methods: {
    ...mapActions('Auth', [
      AuthTypes.FORGOT_PASSWORD,
      AuthTypes.CODECONFIRM,
      AuthTypes.RESET_PASSWORD
    ]),
    validate() {
      //a method on "change" hook to improve performance compared to 'computed' property
      let validated = false;
      // why validate phone nr??
      // if (this.phone) {
      //   //validate phone number
      //   let regex = /^(?:\+371)?\d{8}$/;
      //   if (regex.test(this.phone)) {
      //     this.submitType = 'phone';
      //     validated = true;
      //   }
      // }
      //this.phone.length > 7 ? Vue.set(this, 'invalidPhone', !validated) : Vue.set(this, 'invalidPhone', false);
      Vue.set(this, 'validated', true);
    },
    manageBack() {
      Vue.set(this, 'errorMsg', '');
      switch (this.stage) {
        case 'input':
          this.$router.push({ name: 'Login' });
          break;
        case 'confirmation':
          this.clearConfirm();
          Vue.set(this, 'stage', 'input');
          break;
        case 'password':
          this.clearPassword();
          Vue.set(this, 'stage', 'input');
          break;
      }
    },
    manageInput() {
      let data = {
        type: this.submitType
      };
      data.phone_number = this.phone;

      this.FORGOT_PASSWORD(data).then((res) => {
        if (res.data && res.data.message && res.data.message.confirmation_code) {
          Vue.set(this, 'errorMsg', '');
          Vue.set(this, 'stage', 'confirmation');
        } else {
          if (res.data && res.data.data && res.data.data.errors) {
            if (res.data.data.errors.phone_number) {
              Vue.set(this, 'errorMsg', 'phoneNotFound');
            }
          }
        }
      });
    },
    validateConfirm() {
      let validated = false;
      let confirmCode = this.confirmationCode;
      if (confirmCode) {
        confirmCode = confirmCode.toUpperCase();
        let regex = /^[0-9A-Z]{6}$/;
        if (regex.test(confirmCode)) {
          validated = true;
        }
      }
      Vue.set(this, 'confirmationCode', confirmCode);
      Vue.set(this, 'legalConfirm', validated);
    },
    clearConfirm() {
      Vue.set(this, 'confirmationCode', '');
    },
    manageConfirm() {
      let data = {
        code: this.confirmationCode
      };
      this.CODECONFIRM(data).then((res) => {
        if (res.data && res.data.message && res.data.message.success) {
          Vue.set(this, 'errorMsg', '');
          Vue.set(this, 'stage', 'password');
          Vue.set(this.user, 'phone', this.phone)
        } else {
          if (res.data.data.errors.code) {
            Vue.set(this, 'errorMsg', 'codeNotFound');
          } else if (res.data.data.errors.expired) {
            Vue.set(this, 'errorMsg', 'codeExpired');
          }
        }
      });
    },
    validatePassword() {
      let validated = true;
      // matching password
      if (!this.user.password || !this.user.password_confirmation) {
        validated = false;
      } else {
        if (this.user.password && this.user.password.length < 6) {
          validated = false;
          Vue.set(this, 'tooShort', true);
        } else {
          Vue.set(this, 'tooShort', false);
        }
        if (this.user.password !== this.user.password_confirmation) {
          validated = false;
          Vue.set(this, 'noMatch', true);
        } else {
          Vue.set(this, 'noMatch', false);
        }
      }
      Vue.set(this, 'passwordValidated', validated);
    },
    managePassword() {
      // dont pass the other value to backend if not neccessary
      let user = {};
      user.phone_number = this.user.phone;
      user.password = this.user.password;
      user.password_confirmation = this.user.password_confirmation;
      user.confirmation_code = this.confirmationCode ? this.confirmationCode : '';
      this.RESET_PASSWORD(user).then((res) => {
        if (res.data && res.data.message && res.data.message.success) {
          this.$refs["forgot-password-modal-confirm"].show();
        } else if (res.data && res.data.data && res.data.data.errors) {
          Vue.set(this, 'errorMsg', 'passwordError');
        }
      });
    },
    onConfirmModalClose () {
      this.$refs["forgot-password-modal-confirm"].hide();
      this.$router.push({ name: 'Login' });
    },
    clearPassword() {
      let user = {
        phone: '',
        password: '',
        password_confirmation: ''
      };
      Vue.set(this, 'user', user);
    },
  }
};

</script>

<style lang="scss">

.forgot-password-wrapper {
  background-color: #E6F0EE;
}

.forgot-password-card {
  border-radius: 4rem;
  padding: 3rem 4rem;
  background-color: #fff;
  box-shadow: 0px 7px 23px 5px rgba(0, 0, 0, 0.1);

  .forgot-password-title {
    text-transform: uppercase;
    font-weight: 900;
    text-align: center;
  }
}
</style>
